import { adsConfig, canBootstrapAds, isLiveAdsOrigin, type AdsConfig } from './ads-config';
import './ad-consent';

const scriptLoads = new Map<string, Promise<void>>();
export function loadExternalScript(id: string, src: string, crossOrigin?: 'anonymous'): Promise<void> {
  const pending = scriptLoads.get(id);
  if (pending) return pending;
  const promise = new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) {
      reject(new Error(`Unexpected existing script: ${id}`));
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    if (crossOrigin) script.crossOrigin = crossOrigin;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Unable to load ${id}`));
    document.head.appendChild(script);
  });
  // Failures remain failed for this document; no retry loop or duplicate requests.
  scriptLoads.set(id, promise);
  return promise;
}

function isAllowedDocument(config: AdsConfig): boolean {
  return typeof window !== 'undefined' && canBootstrapAds(config) &&
    isLiveAdsOrigin(window.location.origin) && navigator.globalPrivacyControl !== true;
}

/** Bootstrap the account-provided CMP independently of the advertising script. */
export async function loadPrivacyMessage(config: AdsConfig = adsConfig): Promise<void> {
  if (!isAllowedDocument(config)) throw new Error('Advertising is not configured for this document');
  window.googlefc = window.googlefc || { callbackQueue: [] as Array<Record<string, () => void>> };
  window.googlefc.callbackQueue = window.googlefc.callbackQueue || ([] as Array<Record<string, () => void>>);
  // Marker used by Google's generated standalone Privacy & messaging tag.
  if (!document.querySelector('iframe[name="googlefcPresent"]')) {
    const marker = document.createElement('iframe');
    marker.name = 'googlefcPresent';
    marker.title = 'Privacy messaging availability';
    marker.hidden = true;
    marker.setAttribute('aria-hidden', 'true');
    marker.tabIndex = -1;
    document.body.appendChild(marker);
  }
  await loadExternalScript('airports-google-cmp', config.cmpScriptUrl);
}

export async function loadAdSense(config: AdsConfig = adsConfig): Promise<void> {
  if (!isAllowedDocument(config)) throw new Error('Advertising is not configured for this document');
  if (window.adsbygoogle?.pauseAdRequests === 1) throw new Error('Advertising consent is paused');
  window.adsbygoogle = window.adsbygoogle || [];
  window.adsbygoogle.requestNonPersonalizedAds = 1;
  await loadExternalScript('airports-adsense', `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.publisherId}`, 'anonymous');
}

const requestedUnits = new WeakSet<HTMLElement>();
const requestedPages = new Set<string>();
export function requestAdUnit(element: HTMLElement): boolean {
  const pageKey = window.location.pathname.replace(/\/+$/, '') || '/';
  if (window.adsbygoogle?.pauseAdRequests === 1) return false;
  if (requestedPages.has(pageKey) || requestedUnits.has(element) || element.dataset.adsbygoogleStatus || !element.isConnected) return false;
  if (element.getBoundingClientRect().width < 250) return false;
  requestedUnits.add(element);
  requestedPages.add(pageKey);
  try {
    window.adsbygoogle = window.adsbygoogle || [];
    window.adsbygoogle.requestNonPersonalizedAds = 1;
    window.adsbygoogle.push({});
    return true;
  } catch { return false; }
}
